import React, { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api, { API_BASE, formatApiError } from "@/api/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Save, KeyRound, Upload as UploadIcon } from "lucide-react";
import { fmtDate } from "@/lib/format";

function initials(n = "") {
    return n.split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export default function Profile() {
    const { user, refresh } = useAuth();
    const [me, setMe] = useState(null);
    const [pw, setPw] = useState({ current_password: "", new_password: "" });
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/auth/me").then((r) => setMe(r.data));
    }, []);

    if (!me) return <div className="text-slate-500 text-sm">Loading…</div>;

    const saveProfile = async () => {
        try {
            await api.patch("/auth/profile", {
                name: me.name,
                phone: me.phone,
                avatar_url: me.avatar_url,
            });
            toast.success("Profile updated");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const savePw = async () => {
        try {
            await api.post("/auth/change-password", pw);
            toast.success("Password updated");
            setPw({ current_password: "", new_password: "" });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const uploadAvatar = async (file) => {
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/documents/upload?category=avatar`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            const token = localStorage.getItem("ca_access_token");
            const url = `${API_BASE}/documents/${data.id}/download?_t=${token?.slice(-6)}`;
            setMe({ ...me, avatar_url: url });
            toast.success("Photo uploaded — remember to Save changes");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="space-y-6" data-testid="profile-page">
            <PageHeader title="My Profile" subtitle="Personal details and account security" />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="p-6 lg:col-span-2">
                    <div className="flex items-center gap-4">
                        <Avatar className="h-16 w-16">
                            {me.avatar_url ? <AvatarImage src={me.avatar_url} alt={me.name} /> : null}
                            <AvatarFallback className="bg-primary text-white text-lg font-semibold">
                                {initials(me.name)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <div className="font-display text-xl font-semibold text-slate-900">{me.name}</div>
                            <div className="text-sm text-slate-500">{me.email}</div>
                            <Button
                                variant="outline"
                                size="sm"
                                className="mt-2"
                                onClick={() => fileRef.current?.click()}
                                data-testid="upload-avatar"
                            >
                                <UploadIcon size={13} className="mr-1.5" /> Change photo
                            </Button>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => e.target.files?.[0] && uploadAvatar(e.target.files[0])}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-6">
                        <F label="Full name">
                            <Input value={me.name || ""} onChange={(e) => setMe({ ...me, name: e.target.value })} data-testid="profile-name" />
                        </F>
                        <F label="Phone">
                            <Input value={me.phone || ""} onChange={(e) => setMe({ ...me, phone: e.target.value })} data-testid="profile-phone" />
                        </F>
                        <ReadOnly label="Email" value={me.email} />
                        <ReadOnly label="Role" value={me.role} />
                        <ReadOnly label="Designation" value={me.designation || "—"} />
                        <ReadOnly label="Department" value={me.department || "—"} />
                        <ReadOnly label="Reporting manager" value={me.reporting_manager || "—"} />
                        <ReadOnly label="Joined" value={me.joining_date ? fmtDate(me.joining_date) : "—"} />
                    </div>

                    <div className="mt-6">
                        <Button onClick={saveProfile} className="bg-primary hover:bg-primary/90" data-testid="save-profile">
                            <Save size={14} className="mr-1.5" /> Save changes
                        </Button>
                    </div>
                </Card>

                <Card className="p-6">
                    <h3 className="font-display text-lg font-semibold flex items-center gap-2">
                        <KeyRound size={16} /> Change password
                    </h3>
                    <div className="mt-4 space-y-3">
                        <F label="Current password">
                            <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
                        </F>
                        <F label="New password">
                            <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
                        </F>
                        <Button onClick={savePw} className="bg-primary hover:bg-primary/90 w-full" data-testid="save-password">
                            Update password
                        </Button>
                    </div>
                    <div className="mt-6 pt-6 border-t border-slate-100 text-xs text-slate-500">
                        <div className="uppercase font-semibold tracking-widest text-[10px] mb-1">Your permissions</div>
                        <div className="flex flex-wrap gap-1">
                            {(me.permissions || []).map((p) => (
                                <span key={p} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                                    {p}
                                </span>
                            ))}
                        </div>
                    </div>
                </Card>
            </div>
        </div>
    );
}

const F = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="field-label">{label}</Label>
        {children}
    </div>
);
const ReadOnly = ({ label, value }) => (
    <div className="space-y-1.5">
        <div className="field-label">{label}</div>
        <div className="text-sm text-slate-700 border border-slate-200 rounded-md px-3 py-2 bg-slate-50 capitalize">
            {String(value || "—").replace("_", " ")}
        </div>
    </div>
);
